import { useEffect, useRef, useState } from "react";
import { crDate, crDateTime } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { BrandAvatar } from "@/components/BrandAvatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Paperclip, Trash2, Download, Upload, Send, FileText, Image as ImageIcon, FileAudio, FileSpreadsheet, Presentation, File as FileIcon, Eye, MessageSquare, Users2 } from "lucide-react";
import { toast } from "sonner";
import ChannelChat from "@/components/ChannelChat";

const ALLOWED_MIME = [
  "image/jpeg", "image/png", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "audio/mpeg", "audio/mp3",
];
const ACCEPT = ALLOWED_MIME.join(",");
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES_PER_OWNER = 5;

function pickIcon(ct) {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("audio/")) return FileAudio;
  if (ct === "application/pdf") return FileText;
  if (ct.includes("sheet") || ct.includes("excel")) return FileSpreadsheet;
  if (ct.includes("presentation") || ct.includes("powerpoint")) return Presentation;
  return FileIcon;
}

function humanSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

async function triggerDownload(file) {
  try {
    // Use the axios api instance so the Bearer header / cookies are sent identically
    const res = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.original_filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error("Error: " + (e?.message || "download"));
  }
}

function isPreviewable(ct) {
  if (!ct) return false;
  if (ct.startsWith("image/")) return true;
  if (ct.startsWith("audio/")) return true;
  if (ct === "application/pdf") return true;
  return false;
}

async function fetchAsBlobUrl(file) {
  const res = await api.get(`/files/${file.id}/view`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

/** Full-screen inline previewer for image/PDF/audio. */
function FilePreviewDialog({ file, open, onOpenChange }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let revoke = null;
    if (open && file && isPreviewable(file.content_type)) {
      setLoading(true);
      fetchAsBlobUrl(file)
        .then((u) => { revoke = u; setBlobUrl(u); })
        .catch(() => toast.error("No se pudo cargar el archivo"))
        .finally(() => setLoading(false));
    } else {
      setBlobUrl(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [open, file?.id]);

  if (!file) return null;
  const ct = file.content_type || "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-8">
            <span className="truncate text-sm font-medium">{file.original_filename}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => triggerDownload(file)}
              data-testid="preview-download-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Descargar
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 flex items-center justify-center bg-slate-100 rounded-md overflow-hidden min-h-[400px]">
          {loading && <Loader2 className="h-8 w-8 animate-spin text-slate-400" />}
          {!loading && blobUrl && ct.startsWith("image/") && (
            <img src={blobUrl} alt={file.original_filename} className="max-h-[70vh] object-contain" data-testid="preview-image" />
          )}
          {!loading && blobUrl && ct === "application/pdf" && (
            <iframe src={blobUrl} title={file.original_filename} className="w-full h-[70vh]" data-testid="preview-pdf" />
          )}
          {!loading && blobUrl && ct.startsWith("audio/") && (
            <audio src={blobUrl} controls className="w-full" data-testid="preview-audio" />
          )}
          {!loading && !isPreviewable(ct) && (
            <div className="p-8 text-center text-sm text-slate-500">
              Este tipo de archivo no se puede previsualizar en el navegador. Descárgalo para abrirlo.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Single attachment chip. Click the name/icon to preview or open inline in a
 *  new tab. A download action is only available from inside the preview modal —
 *  no interaction on the chip auto-triggers a download. */
function AttachmentChip({ file, canDelete, onDelete }) {
  const Icon = pickIcon(file.content_type);
  const [previewOpen, setPreviewOpen] = useState(false);
  const canPreview = isPreviewable(file.content_type);
  const handleOpen = () => {
    if (canPreview) {
      setPreviewOpen(true);
    } else {
      // Non-previewable formats (docx/xlsx/pptx) → open the inline view endpoint
      // in a new tab. Browsers show it in their built-in viewer when they can,
      // otherwise the user gets the browser's own "open with…" prompt.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}/view`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <>
      <div data-testid={`attachment-${file.id}`} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 max-w-full">
        <button
          type="button"
          onClick={handleOpen}
          className="inline-flex items-center gap-2 min-w-0 hover:text-blue-700 text-left"
          title={canPreview ? "Ver" : "Abrir"}
          data-testid={`view-${file.id}`}
        >
          <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <span className="text-xs text-slate-800 truncate" title={file.original_filename}>{file.original_filename}</span>
        </button>
        <span className="text-[10px] text-slate-400 shrink-0">{humanSize(file.size)}</span>
        {canDelete && (
          <button
            type="button"
            data-testid={`delete-attachment-${file.id}`}
            onClick={onDelete}
            className="text-slate-400 hover:text-rose-600 shrink-0"
            title="Eliminar"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <FilePreviewDialog file={file} open={previewOpen} onOpenChange={setPreviewOpen} />
    </>
  );
}

/** Upload zone — button + hidden input. Validates client-side. */
function UploadButton({ disabled, onPick, label, testId }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          if (f.size > MAX_BYTES) { toast.error("Max 10 MB"); return; }
          if (!ALLOWED_MIME.includes(f.type)) { toast.error("Unsupported file type"); return; }
          onPick(f);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={testId}
        disabled={disabled}
        onClick={() => ref.current?.click()}
      >
        <Upload className="h-3.5 w-3.5 mr-1.5" />
        {label}
      </Button>
    </>
  );
}

export default function HomeworkWall({ homework, initialStudentId = null }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [material, setMaterial] = useState([]);
  const [subs, setSubs] = useState([]);
  const [myText, setMyText] = useState("");
  const [savingText, setSavingText] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null); // submission_id | "material"
  const [groupStudents, setGroupStudents] = useState(0);
  const [tab, setTab] = useState("private");

  const isAdmin = user?.role === "superadmin" || user?.role === "academic_director";
  const isTeacher = user?.role === "teacher";
  const isStudent = user?.role === "student";
  const isOwnerTeacher = isTeacher && homework?.teacher_id === user?.user_id;

  const refresh = async () => {
    if (!homework?.id) return;
    setLoading(true);
    try {
      const [mr, sr] = await Promise.all([
        api.get(`/homework/${homework.id}/material`),
        api.get(`/homework/${homework.id}/submissions`),
      ]);
      setMaterial(mr.data || []);
      setSubs(sr.data || []);
      if (isStudent) {
        const mine = (sr.data || []).find((s) => s.student_id === user.user_id);
        setMyText(mine?.text || "");
      }
      // Mark this homework as seen for the current viewer (best-effort, non-blocking)
      api.post(`/homework/${homework.id}/mark-seen`).catch(() => {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [homework?.id]);

  // Detect group size to show/hide the "Canal general" tab
  useEffect(() => {
    if (!homework?.group_id) return;
    api.get("/enrollments", { params: { group_id: homework.group_id } })
      .then((r) => setGroupStudents((r.data || []).length))
      .catch(() => {});
  }, [homework?.group_id]);

  const showChannel = groupStudents >= 2;

  const mySubmission = isStudent ? subs.find((s) => s.student_id === user.user_id) : null;

  const saveMyText = async () => {
    setSavingText(true);
    try {
      await api.post(`/homework/${homework.id}/submissions`, { text: myText });
      await refresh();
      toast.success(t("submission_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSavingText(false);
    }
  };

  const uploadMaterial = async (f) => {
    setUploadingFor("material");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/homework/${homework.id}/material`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setUploadingFor(null); }
  };

  const uploadToSubmission = async (subId, f) => {
    setUploadingFor(subId);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/homework/${homework.id}/submissions/${subId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setUploadingFor(null); }
  };

  const uploadToMyNewSubmission = async (f) => {
    setUploadingFor("new-submission");
    try {
      const sub = await api.post(`/homework/${homework.id}/submissions`, { text: myText || null });
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/homework/${homework.id}/submissions/${sub.data.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setUploadingFor(null); }
  };

  const deleteFile = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggleReaction = async (subId, emoji) => {
    try {
      await api.post(`/homework/submissions/${subId}/reactions`, { emoji });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const addComment = async (subId, text) => {
    try {
      await api.post(`/homework/submissions/${subId}/comments`, { text });
      await refresh();
      toast.success(t("comment_posted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/homework/comments/${commentId}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return <div className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="homework-wall">
      <ChannelChat homework={homework} canWrite={true} />
    </div>
  );
}


const EMOJIS = ["👏", "❤️", "✅", "🎉", "🤔"];

function SubmissionCard({ s, user, t, canDelete, canComment, onReact, onComment, onDeleteComment, onDeleteFile, highlight = false, defaultCollapsed = false }) {
  const [commentText, setCommentText] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [posting, setPosting] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const isMine = s.student_id === user.user_id;
  const totalReactions = EMOJIS.reduce((sum, e) => sum + (s.reactions?.[e] || 0), 0);

  // Auto-scroll into view when this card matches an initialStudentId
  const cardRef = useRef(null);
  useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);

  // "Nuevo" flags — activity newer than the viewer's last_seen (server-provided).
  const lastSeen = s.viewer_last_seen || null;
  const submissionIsNew = !isMine && (!lastSeen || (s.updated_at && s.updated_at > lastSeen));
  // Comments sorted newest-first with a "Nuevo" flag for anyone-but-me
  const orderedComments = [...(s.comments || [])].sort(
    (a, b) => (b.created_at || "").localeCompare(a.created_at || "")
  );
  const attachmentsCount = s.attachments?.length || 0;
  const commentsCount = orderedComments.length;

  return (
    <Card
      ref={cardRef}
      className={`p-4 border ${
        highlight ? "border-blue-500 shadow-md ring-2 ring-blue-200" :
        submissionIsNew ? "border-blue-400 shadow-sm ring-1 ring-blue-100" :
        "border-slate-200"
      }`}
      data-testid={`submission-${s.id}`}
    >
      <div className="flex items-start gap-3">
        <BrandAvatar src={s.student_picture} alt={s.student_name} className="h-9 w-9 text-sm shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-900">
                {isMine ? `${s.student_name} (${t("you")})` : s.student_name}
              </span>
              {submissionIsNew && (
                <span className="text-[9px] uppercase font-bold tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" data-testid={`new-badge-${s.id}`}>
                  Nuevo
                </span>
              )}
              <span className="text-[10px] text-slate-400">
                {s.updated_at ? crDateTime(s.updated_at) : ""}
              </span>
              {collapsed && (
                <span className="text-[10px] text-slate-500">
                  · {attachmentsCount > 0 && `${attachmentsCount} 📎 · `}{commentsCount} 💬
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="text-[10px] text-slate-500 hover:text-slate-900 hover:underline"
              data-testid={`toggle-collapsed-${s.id}`}
            >
              {collapsed ? "Expandir hilo" : "Contraer"}
            </button>
          </div>
          {!collapsed && s.text && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{s.text}</p>
          )}
          {!collapsed && s.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {s.attachments.map((f) => (
                <AttachmentChip
                  key={f.id}
                  file={f}
                  canDelete={canDelete(f)}
                  onDelete={() => onDeleteFile(f.id)}
                />
              ))}
            </div>
          )}

          {/* Reactions row */}
          {!collapsed && (
          <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-2 border-t border-slate-100">
            {EMOJIS.map((emoji) => {
              const count = s.reactions?.[emoji] || 0;
              const mine = s.my_reactions?.includes(emoji);
              return (
                <button
                  key={emoji}
                  type="button"
                  data-testid={`react-${s.id}-${emoji}`}
                  onClick={() => onReact(emoji)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors ${
                    mine
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span>{emoji}</span>
                  {count > 0 && <span className="font-medium">{count}</span>}
                </button>
              );
            })}
            {totalReactions > 0 && (
              <span className="text-[10px] text-slate-400 ml-1">
                {totalReactions} {totalReactions === 1 ? t("reaction") : t("reactions")}
              </span>
            )}
          </div>
          )}

          {/* Comments */}
          {!collapsed && (orderedComments.length > 0 || canComment) && (
            <div className="mt-3 space-y-2" data-testid={`comments-${s.id}`}>
              {orderedComments.map((c) => {
                const commentIsNew = c.author_user_id !== user.user_id && (!lastSeen || (c.created_at && c.created_at > lastSeen));
                return (
                <div key={c.id} className={`flex items-start gap-2 p-2.5 rounded-md border ${commentIsNew ? "bg-blue-50 border-blue-300 ring-1 ring-blue-100" : "bg-amber-50/60 border-amber-200/70"}`}>
                  <BrandAvatar src={c.author_picture} alt={c.author_name} className="h-6 w-6 text-[10px] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-xs font-semibold text-slate-900">{c.author_name}</span>
                      <span className="text-[9px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        {c.author_role === "teacher" ? t("teacher") : t("admin")}
                      </span>
                      {commentIsNew && (
                        <span className="text-[9px] uppercase font-bold tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          Nuevo
                        </span>
                      )}
                      <span className="text-[10px] text-slate-400">
                        {crDateTime(c.created_at)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{c.text}</div>
                  </div>
                  {(c.author_user_id === user.user_id || user.role === "superadmin" || user.role === "academic_director") && (
                    <button
                      type="button"
                      data-testid={`delete-comment-${c.id}`}
                      onClick={() => onDeleteComment(c.id)}
                      className="text-slate-400 hover:text-rose-600 shrink-0"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );})}
              {canComment && !showCommentBox && (
                <button
                  type="button"
                  data-testid={`open-comment-${s.id}`}
                  onClick={() => setShowCommentBox(true)}
                  className="text-xs text-blue-700 hover:underline font-medium"
                >
                  + {isMine ? "Responder o hacer pregunta" : t("add_feedback")}
                </button>
              )}
              {canComment && showCommentBox && (
                <div className="space-y-2">
                  <Textarea
                    data-testid={`comment-input-${s.id}`}
                    placeholder={isMine ? "Escribe tu respuesta o pregunta al profesor…" : t("write_feedback_placeholder")}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows={2}
                    className="resize-none text-sm"
                    maxLength={1000}
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      data-testid={`submit-comment-${s.id}`}
                      disabled={posting || !commentText.trim()}
                      onClick={async () => {
                        setPosting(true);
                        try { await onComment(commentText); setCommentText(""); setShowCommentBox(false); }
                        finally { setPosting(false); }
                      }}
                      className="h-7 text-xs bg-brand-blue hover:bg-brand-blue/90 text-white"
                    >
                      {posting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      {isMine ? "Enviar" : t("send_feedback")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => { setShowCommentBox(false); setCommentText(""); }}
                      className="h-7 text-xs"
                    >
                      {t("cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
